<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="showLoader || (!showLoader && !applyErrorState && (!getBeneficiariesList.length || !filteredBeneficiariesList.length))"
    :showLoader="showLoader"
	:showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    :showSearchSlot="!showLoader && getBeneficiariesList.length && !applyErrorState"
    :showTopRightAreaSlot="getBeneficiariesList.length"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- We are using this slot, even tho the name is not that clear, it's the position we need for the header -->
        <template #accounts-card-slot-area>
            <button-primary
            v-if="!!getBeneficiariesList.length"
            icon="add-user"
            buttonSize="medium"
            :label="t('transfers.beneficiaries.emptyState.createBeneficiary')"
            @clicked="navigateByName(routeList.TRANSFERS_BENEFICIARIES_CREATE_NAME)" />
        </template>
        <!-- *********************************************
        *                    Search Bar                  *
        ********************************************** -->
        <template #top-search-area-slot>
            <input-search
            v-if="!showLoader && getBeneficiariesList.length && !applyErrorState"
            :params="{ placeholder: t('transfers.beneficiaries.searchPlaceholder')}"
            :inputValue="searchValue"
            :preventInputClick="getMobileAppState"
            @input="searchValue = $event"
            @clicked="showMobileSearch = true"
            @rightButtonClicked="searchValue = ''"/>
        </template>
        <!-- *********************************************
        *												 *
        *                      Content                   *
        *												 *
        ********************************************** -->
        <transition name="common-enter-animation" mode="out-in" appear>
            <div v-if="!showLoader && filteredBeneficiariesList.length && !applyErrorState" class="content-wrapper prevent-auto-margins">
                <!-- Table Generic -->
                <table-generic
                :headerData="beneficiariesTableHeaderData"
                :tableStyles="beneficiariesTableStyles"
                :tableData="filteredBeneficiariesList"
                @sortBy="sortByInfo = $event"
                @rowClicked="beneficiariesClickHandler($event)"
                @buttonClicked="redirectToEditFeature"
                @moreOptionsClicked="moreOptionsClicked"/>
            </div>
        </transition>
        <template #empty-state-slot>
            <!-- Benificiaries Empty State -->
            <empty-state-generic
            v-if="!showLoader && !applyErrorState && (!getBeneficiariesList.length || !filteredBeneficiariesList.length)"
            illustration="emptyState/beneficiaries"
            buttonIcon="add-user"
            :buttonLabel="t('transfers.beneficiaries.emptyState.createBeneficiary')"
            :title = emptyStateTitle
            :description = emptyStateDescription
            :hasButton="!getBeneficiariesList.length"
            @clicked="navigateByName(routeList.TRANSFERS_BENEFICIARIES_CREATE_NAME)"/>
        </template>
    </wrapper-features-templates-one-column>
    <!-- Beneficiaries Detail Side Modal -->
    <detail-modal
    v-if="showDetailsModal"
    :selectedBeneficiary="selectedBeneficiary"
    @hideMe="showDetailsModal = false"
    @redirectToEditFeature="redirectToEditFeature($event)"
    @deleteBeneficiary="showCenteredModal = true"/>
    <!-- Search Modal for Mobile -->
    <modal-side-search
    v-if="showMobileSearch"
    clearSearchValueOnClose
    :searchPlaceholder="t('transfers.beneficiaries.searchPlaceholder')"
    :searchInputValue="searchValue"
    :triggerClose="triggerMobileSearchClose"
    :applyEmptyState="!filteredBeneficiariesList.length"
    @updateSearchValue="searchValue = $event"
    @hideMe="[showMobileSearch = false, triggerClose = false, searchValue = '']">
        <!-- Table Generic -->
        <table-generic
        :headerData="beneficiariesTableHeaderData"
        :tableStyles="beneficiariesTableStyles"
        :tableData="filteredBeneficiariesList"
        @sortBy="sortByInfo = $event"
        @rowClicked="beneficiariesClickHandler($event)"
        @buttonClicked="redirectToEditFeature"
        @moreOptionsClicked="moreOptionsClicked"/>
    </modal-side-search>
    <!-- Delete Beneficiary Modal -->
    <modal-centered-generic
    v-if="showCenteredModal"
    negative
    rightButtonIcon="check-line"
    titleIcon="delete"
    :title="t('transfers.beneficiaries.deleteBeneficiaryModal.title')"
    :text="t('transfers.beneficiaries.deleteBeneficiaryModal.text', { personName: selectedBeneficiary.name })"
    :leftButtonText="t('transfers.beneficiaries.deleteBeneficiaryModal.leftButtonText')"
    :rightButtonText="t('transfers.beneficiaries.deleteBeneficiaryModal.rightButtonText')"
    @hideMe="showCenteredModal = false"
    @rightButtonClicked="deleteBeneficiaries"/>
    <!-- Feedback Toasts -->
    <toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
/* Generic Imports */
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import InputSearch from '@/components/generic/inputs/Search'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import DetailModal from '@/components/features/transfers/beneficiaries/consult/DetailModal'
import ModalSideSearch from '@/components/generic/modals/side/Search'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import ButtonPrimary from '@/components/generic/buttons/Primary'

/* Assets & Helpers */
import { ref, computed, onBeforeUnmount } from 'vue'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { getDate } from '@/assets/scripts/dateBuilders'
import { sortTableData } from '@/assets/scripts/parsers/tables'
import { ACCOUNTS_NIB_IBAN_GENERIC } from '@/assets/constants/masks'
import { errorObjectConstructor, sucessObjectConstrutor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestBeneficiariesList, deleteBeneficiary, clearFeatureStates } = mapActions(moduleMapper.TRANSFERS_BENEFICIARIES)
const { getBeneficiariesList, getMobileAppState } = mapGetters(moduleMapper.TRANSFERS_BENEFICIARIES)

/* Data */
const searchValue = ref('')
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const applyErrorState = ref(false)
const selectedBeneficiary = ref(null)
const showCenteredModal = ref(false)
const showMobileSearch = ref(false)
const triggerMobileSearchClose = ref(false)

const emptyStateTitle = computed(() => {
    return getBeneficiariesList.value.length
        ? t('transfers.beneficiaries.emptyState.genericTitle')
        : t('transfers.beneficiaries.emptyState.noBenificiariesTitle')
})

const emptyStateDescription = computed(() => {
    return getBeneficiariesList.value.length
        ? t('transfers.beneficiaries.emptyState.genericDescription')
        : t('transfers.beneficiaries.emptyState.noBenificiariesDescription')
})

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const requestBeneficiaries = async () => {
    showLoader.value = true
    applyErrorState.value = false
	showFeedbackToast.value = false
	cancelAllPendingRequests()

    try {
        await requestBeneficiariesList()
        showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		applyErrorState.value = true
		errorHandler(error, requestBeneficiaries)
	}
}

const deleteBeneficiaries = async () => {
    showOverlayLoader.value = true
    applyErrorState.value = false
    showDetailsModal.value = false

    try {
        const response = await deleteBeneficiary(selectedBeneficiary.value.id)
        showOverlayLoader.value = false
        showCenteredModal.value = false
        requestBeneficiariesList()
        successHandler(response.data)
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error, deleteBeneficiaries)
    }
}

/*************************************************
*                                                *
*               Beneficiaries Data               *
*                                                *
*************************************************/
const showDetailsModal = ref(false)
const sortByInfo = ref()

const redirectToEditFeature = (selectedBeneficiary) => {
    let beneficiary = selectedBeneficiary

    if (selectedBeneficiary.rowId) beneficiary = getBeneficiariesList.value.find(obj => obj.id === selectedBeneficiary.rowId)

    navigateByName(routeList.TRANSFERS_BENEFICIARIES_EDIT_NAME, { selectedBeneficiary: beneficiary })
}

const tableMoreOptions = () => {
    const moreOptions = [
        {
            id: 2,
            text: t('transfers.beneficiaries.moreOptions.delete'),
            icon: 'delete',
            variant: 'negative',
            callbackFunction: () => { showCenteredModal.value = true }
        }
    ]

    if (getMobileAppState.value) {
        moreOptions.unshift({
            id: 1,
            text: t('transfers.beneficiaries.moreOptions.edit'),
            icon: 'pencil',
            callbackFunction: redirectToEditFeature
        })
    }
    return moreOptions
}

const beneficiariesTableHeaderData = [
    { label: t('transfers.beneficiaries.tableHeaderData.name'), showInMobile: { groupWith: 'genericDestination' }, dataAttr: 'name', sortable: true, initialSortDirection: 'Asc', rowCellType: 'title' },
    { label: t('transfers.beneficiaries.tableHeaderData.phoneNumber'), dataAttr: 'phoneNumber', sortable: true },
    { label: t('transfers.beneficiaries.tableHeaderData.genericDestination'), dataAttr: 'genericDestination', sortable: true },
    { label: t('transfers.beneficiaries.tableHeaderData.creationDate'), dataAttr: 'creationDate', sortable: true },
    { dataAttr: 'button', rowCellType: 'button' },
    { dataAttr: 'tableOptions', showInMobile: true, rowCellType: 'more-options' }
]

const beneficiariesTableStyles = [
    getMobileAppState.value ? null : { width: '35%', 'min-width': '35%' },
    { 'text-align': 'left' },
    { width: '250px', 'min-width': '250px', 'text-align': 'left' },
    { 'text-align': 'left' },
    null,
    null
]

const beneficiariesTableData = computed(() => {
    let tableData = getBeneficiariesList.value.map(beneficiary => {
        return [
            { value: beneficiary.id, dataAttr: 'id' },
            { type: 'title', value: beneficiary.name, dataAttr: 'name' },
            { value: beneficiary.phoneNumber ? beneficiary.phoneNumber : '-', dataAttr: 'phoneNumber' },
            {
                value: {
                    content: beneficiary.genericDestination ? beneficiary.genericDestination : '-',
                    mask: beneficiary.genericDestination ? ACCOUNTS_NIB_IBAN_GENERIC : null,
                    classes: getMobileAppState.value ? 'secondary-color' : null
                },
                rawValue: beneficiary.genericDestination,
                dataAttr: 'genericDestination'
            },
            {
                type: 'description',
                value: { content: getDate(beneficiary.creationDate), sortByValue: beneficiary.creationDate, classes: 'capitalize' },
                dataAttr: 'creationDate'
            },
            { type: 'button', value: 'pencil', dataAttr: 'button' },
            {
                type: 'more-options',
                value: tableMoreOptions(),
                dataAttr: 'tableOptions',
                hidden: false
            }
        ]
    })
    if (sortByInfo.value) {
        tableData = sortTableData(tableData, sortByInfo.value)
    }
    return tableData
})

const filteredBeneficiariesList = computed(() => {
  return beneficiariesTableData.value.filter(name => name.find(el => el.dataAttr === 'name').value.toLowerCase().includes(searchValue.value.toLowerCase()))
})

const beneficiariesClickHandler = row => {
    const beneficiary = getBeneficiariesList.value.find(ben => ben.id === row.rowId)

    selectedBeneficiary.value = beneficiary
    showDetailsModal.value = true
}

const moreOptionsClicked = row => {
	selectedBeneficiary.value = getBeneficiariesList.value.find(ben => ben.id === row.rowId)

	tableMoreOptions().find(obj => obj.id === row.optionClicked).callbackFunction(selectedBeneficiary.value)
}

/*************************************************
*                                                *
*                 Error Feedback                 *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const successHandler = (data) => {
    feedbackToastData.value = sucessObjectConstrutor(data.message)
	showFeedbackToast.value = true
}
const errorHandler = (data, retryFn) => {
    showOverlayLoader.value = false
    showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

requestBeneficiaries()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.search-box {
    width: 100%;
    height: 56px;
    margin-bottom: 16px;

    flex-shrink: 0;
    background-color: var(--white);
    box-shadow: 0 10px 16px 0 rgba(0, 50, 90, 0.08);
    border-radius: 12px;
}

.content-wrapper {
	@include commonEnteringAnimation;

    width: 100%;
}
</style>
